import { useState, useEffect, useMemo } from "react";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import {
  fetchAvailabilityData,
  GroupedAvailability,
} from "../../../components/SupplierInventorySearch/utils/typesenseUtils";
import { usePartAvailabilityStore } from "../store/usePartAvailabilityStore";

export interface OrderItems {
  rootPartNumber: string;
  partNumber: string;
  orderedQuantity: number;
  partName: string;
  remainingDecisionQuantity: number;
}

export interface UsePartAvailabilityProps {
  orderId: string;
  orderItems: Array<OrderItems>;
  customerGeoPoint?: { latitude: number; longitude: number };
  enabled?: boolean;
}

export const usePartAvailability = ({
  orderId,
  orderItems,
  customerGeoPoint,
  enabled = false,
}: UsePartAvailabilityProps) => {
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    getAvailabilityData,
    setAvailabilityData,
    getLastFetchTime,
    setLastFetchTime,
  } = usePartAvailabilityStore();

  const availabilityData = getAvailabilityData(orderId) || [];
  const lastFetchTime = getLastFetchTime(orderId);

  const transformedOrderItems = useMemo(() => {
    return orderItems.map((item) => ({
      rootPartNumber: item.rootPartNumber,
      partNumber: item.partNumber,
      quantity: item.remainingDecisionQuantity,
      partName: item.partName,
    }));
  }, [orderItems]);

  const fetchAvailability = async () => {
    if (!mobileConfigs || transformedOrderItems.length === 0) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await fetchAvailabilityData(
        transformedOrderItems,
        {
          typesense_api_key:
            mobileConfigs?.typesense_api_key_supplierinventories,
          typesense_host: mobileConfigs?.typesense_host_supplierinventories,
          supplier_discovery_suppliers_limit:
            mobileConfigs?.supplier_discovery_suppliers_limit,
          supplier_discovery_parts_limit:
            mobileConfigs?.supplier_discovery_parts_limit,
        },
        customerGeoPoint
      );

      setAvailabilityData(orderId, data);
      setLastFetchTime(orderId, new Date());
    } catch (err) {
      console.error("Error fetching part availability:", err);
      setError("Failed to fetch part availability data");
    } finally {
      setLoading(false);
    }
  };

  const availabilityMap = useMemo(() => {
    const map = new Map<string, GroupedAvailability>();
    availabilityData.forEach((item) => {
      map.set(item.rootPartNumber, item);
    });
    return map;
  }, [availabilityData]);

  return {
    availabilityData,
    availabilityMap,
    availabilityLoading: loading,
    availabilityError: error,
    fetchAvailability,
    transformedOrderItems,
    lastFetchTime,
  };
};
